import localForage from 'localforage';
import { persistReducer } from 'redux-persist';
import { createSelector } from '@reduxjs/toolkit';
import { type RootState } from 'app/store';
import { type StorageState, type StorageActionTypes } from 'app/store/types';
import _ from '@lodash';

const initialState: StorageState = {};

const storageReducer = (state = initialState, action: StorageActionTypes) => {
	switch (action.type) {
		case 'GET_LICENSE_GROUP_DATA_SUCCESS': {
			const {
				licenseGroupId,
				data: { archivedJobs, myFiles },
			} = action.payload;

			return {
				...state,
				[licenseGroupId]: {
					...state[licenseGroupId],
					...(archivedJobs && { archivedJobsById: archivedJobs }),
					...(myFiles && { myFilesById: myFiles }),
				},
			};
		}
		case 'LOGGED_OUT_USER':
		case 'PURGE_STATE':
			return initialState;
		default: {
			return state;
		}
	}
};

export default persistReducer(
	{
		key: 'storage',
		storage: localForage,
		whitelist: localStorage.getItem('persist') ? undefined : [],
	},
	storageReducer,
);

// Selectors
// all archived jobs (including trashed)
const getAllArchivedJobsById = ({ app: { selectedLicenseGroupId }, storage }: RootState) =>
	storage[selectedLicenseGroupId]?.archivedJobsById;

export const getAllArchivedJobs = createSelector([getAllArchivedJobsById], (allArchivedJobsById) =>
	Object.values(allArchivedJobsById),
);

// archived jobs only (excluding trashed)
export const getArchivedJobsById = createSelector([getAllArchivedJobsById], (allArchivedJobsById) =>
	_.pickBy(allArchivedJobsById, (job) => !job.trashedArchive),
);

export const getArchivedJobs = createSelector([getArchivedJobsById], (archivedJobsById) =>
	Object.values(archivedJobsById),
);

// trashed jobs only
export const getTrashedJobsById = createSelector([getAllArchivedJobsById], (allArchivedJobsById) =>
	_.pickBy(allArchivedJobsById, (job) => job.trashedArchive),
);

export const getTrashedJobs = createSelector([getTrashedJobsById], (trashedJobsById) => Object.values(trashedJobsById));

export const getArchivedJobById = (id: string) => (state: RootState) => getArchivedJobsById(state)?.[id];

export const getTrashedJobById = (id: string) => (state: RootState) => getTrashedJobsById(state)?.[id];

export const getMyFilesById = ({ app: { selectedLicenseGroupId }, storage }: RootState) =>
	storage[selectedLicenseGroupId]?.myFilesById;

export const getMyFiles = createSelector([getMyFilesById], (myFilesById) => Object.values(myFilesById));
